import { AUSTRALIA_PRODUCTION_SITE_URL } from "./storefrontEnvironment.js";

export const FLYPAY_PAY_TYPE = 1900;
export const FLYPAY_CURRENCY = "AUD";

export function isFlyPayPaymentMethod(method = {}) {
  return Number(method.payType ?? method.pay_type) === FLYPAY_PAY_TYPE;
}

export function buildFlyPayPaymentMethods(rawMethods = [], templateKey = "") {
  if (String(templateKey).trim().toLowerCase() !== "australia") return [];

  return rawMethods.filter(isFlyPayPaymentMethod).filter((method) => {
    const active = String(method.activeFlag ?? method.active_flag ?? true).trim().toLowerCase();
    const currency = String(method.payCurrencyCode || method.pay_currency_code || FLYPAY_CURRENCY).trim().toUpperCase();
    return !["false", "0"].includes(active) && currency === FLYPAY_CURRENCY;
  }).map((method) => {
    const methodCode = String(method.methodCode ?? method.paymentMethodCode ?? "").trim();
    return {
      ...method,
      code: methodCode ? `FLYPAY:${methodCode.toUpperCase()}` : "FLYPAY",
      paymentMethodCode: methodCode,
      payType: FLYPAY_PAY_TYPE,
      provider: "flypay",
      name: "FlyPay",
      displayName: "FlyPay",
      currency: FLYPAY_CURRENCY,
      returnType: "redirect",
      iconUrl: "",
      isCard: false,
      isWallet: true,
    };
  });
}

export function resolveFlyPayOrderCode(data = {}) {
  return String(data.orderCode || data.orderBackDTOList?.[0]?.orderCode || data.mallOrderDTOList?.[0]?.orderCode || "").trim();
}

export function getFlyPayShippingValidationError(address = {}) {
  if (!String(address.first_name || "").trim()) return "payment.enterName";
  if (!/^614\d{8}$/.test(String(address.phone || ""))) return "payment.invalidAuPhone";
  if (!address.state || !address.city || !address.address_line1) return "payment.fillCompleteAddress";
  if (!/^\d{4}$/.test(String(address.zipcode || address.postal_code || ""))) return "payment.enterPostalCode";
  if (address.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email)) return "payment.invalidEmailAddress";
  return "";
}

export function buildFlyPayPaymentPayload({
  orderCode, dealerShopId, payOrderCode = "", origin, apiEnvironment = "test", method = {}, terminal = "PC",
  shippingInfo = {}, billingInfo = shippingInfo, items = [], shopperReference = "",
}) {
  if (!String(orderCode || "").trim()) throw new Error("Missing FlyPay order code");
  if (!dealerShopId || (typeof dealerShopId === "number" && !Number.isSafeInteger(dealerShopId))) {
    throw new Error("Missing or invalid FlyPay shop ID");
  }
  const callbackOrigin = String(apiEnvironment).trim().toLowerCase() === "prod"
    ? AUSTRALIA_PRODUCTION_SITE_URL
    : origin;
  const returnUrl = new URL("/payment/successful", callbackOrigin);
  returnUrl.searchParams.set("orderCode", orderCode);
  if (payOrderCode) returnUrl.searchParams.set("payOrderCode", payOrderCode);
  returnUrl.searchParams.set("paymentProvider", "flypay");
  returnUrl.searchParams.set("currency", FLYPAY_CURRENCY);
  const quitUrl = new URL(returnUrl);
  quitUrl.searchParams.set("cancelled", "1");
  const toAddress = (address) => ({
    first_name: address.first_name || "",
    last_name: address.last_name || "",
    country: address.country || "AU",
    state: address.state || "",
    city: address.city || "",
    address_line1: address.address_line1 || "",
    zipcode: address.zipcode || address.postal_code || "",
    email: address.email || "",
    phone: address.phone || "",
  });
  const payload = {
    orderCode: String(orderCode),
    payType: FLYPAY_PAY_TYPE,
    dealerShopId: String(dealerShopId),
    terminal,
    returnPageUrl: returnUrl.toString(),
    quitUrl: quitUrl.toString(),
    shipping_info: toAddress(shippingInfo),
    billing_info: toAddress(billingInfo),
  };
  if (method.paymentMethodCode) payload.paymentMethodCode = method.paymentMethodCode;
  if (shopperReference) payload.shopper_reference = String(shopperReference);
  if (items.length) {
    payload.items = items.map((item) => {
      const quantity = Math.max(1, Number(item.quantity || item.qty || item.buyAmount || 1));
      const lineAmount = Number(item.realSubtotalAmount ?? item.amount ?? item.price ?? 0);
      return {
        name: item.name || item.title || item.dealerItemSpuTitle || "Item",
        price: lineAmount / quantity,
        item_num: quantity,
        unit: "item",
        ...(item.url ? { url: item.url } : {}),
        remark: String(orderCode),
      };
    });
  }
  payload.channel_params = {
    buyer_email: shippingInfo.email || "",
    phone: shippingInfo.phone || "",
    country: shippingInfo.country || "AU",
  };
  return payload;
}

export function resolveFlyPayRedirectUrl(response = {}) {
  if (response.success === false || (response.code && String(response.code) !== "200")) {
    throw new Error(response.msg || response.message || "Failed to create FlyPay payment");
  }
  const redirectUrl = String(response.data?.redirectUrl || "").trim();
  if (!/^https?:\/\//i.test(redirectUrl)) {
    throw new Error(response.data?.failReason || "FlyPay did not return a checkout URL");
  }
  return new URL(redirectUrl).toString();
}

// Match the requested order exactly; an unrelated order must never confirm payment.
export function findFlyPayOrder(response = {}, orderCode = "", payOrderCode = "") {
  if (response.success === false) return null;
  const rows = [];
  const visit = (value) => {
    if (Array.isArray(value)) return value.forEach(visit);
    if (!value || typeof value !== "object") return;
    if (value.orderState !== null && value.orderState !== undefined) rows.push(value);
    for (const key of ["data", "dataList", "list", "records", "orderCodeGroupList", "orderList", "mallOrderDTOList", "dealerOrderDTOList", "orderBackDTOList", "orderInfo", "order", "mallOrderDTO", "dealerOrderDTO"]) {
      if (value[key]) visit(value[key]);
    }
  };
  visit(response.data);
  return orderCode
    ? rows.find((order) => String(order.orderCode || "") === String(orderCode)) || null
    : rows.find((order) => payOrderCode && String(order.payOrderCode || order.payOrderNo || "") === String(payOrderCode)) || null;
}

export function getFlyPayOrderPaymentState(order) {
  if (!order || order.orderState === null || order.orderState === undefined || order.orderState === "") return "pending";
  const state = Number(order.orderState);
  if ([1, 2, 3, 4, 5].includes(state)) return "paid";
  if (state === 6) return "refunded";
  if (state === 7) return "cancelled";
  return "pending";
}
